import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { CheckCheck, Search, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye, AlertCircle } from 'lucide-react';
import { Skeleton } from '../../components/Skeleton';

interface Enquery {
  id: number;
  raison_social: string;
  full_name: string;
  phone: string;
  seen_at: string | null;
}

interface EnqueriesModalProps {
  visible: boolean;
  onHide: () => void;
  onEnqueryMarkedAsSeen: () => void;
}

export function EnqueriesModal({ visible, onHide, onEnqueryMarkedAsSeen }: EnqueriesModalProps) {
  const [enqueries, setEnqueries] = useState<Enquery[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    if (visible) {
      loadEnqueries();
    }
  }, [visible, currentPage, filter]);

  const loadEnqueries = async () => {
    setLoading(true);
    try {
      const response = await api.get('/companies/enqueries/list', {
        params: { page: currentPage, filter }
      });
      setEnqueries(response.data.data);
      setTotalPages(response.data.last_page);
    } catch (error) {
      console.error('Error loading enqueries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsSeen = async (enqueryId: number) => {
    try {
      await api.post('/companies/enqueries/set-seen', { id: enqueryId });
      loadEnqueries();
      onEnqueryMarkedAsSeen();
    } catch (error) {
      console.error('Error marking enquery as seen:', error);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg overflow-scroll max-h-full shadow-xl p-6 w-full max-w-4xl m-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">{t('pending_enqueries')}</h2>
          <button
            onClick={onHide}
            className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
          >
            <X size={24} />
          </button>
        </div>
        <div className="mb-6 relative">
          <input
            type="text"
            placeholder={t('filter_enqueries')}
            className="w-full p-3 pl-10 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        </div>
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="flex items-center space-x-4">
                <Skeleton className="h-10 w-1/4" />
                <Skeleton className="h-10 w-1/4" />
                <Skeleton className="h-10 w-1/4" />
                <Skeleton className="h-10 w-10 rounded-full" />
              </div>
            ))}
          </div>
        ) : enqueries.length === 0 ? (
          <div className="text-center py-8">
            <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-lg font-medium text-gray-900">{t('no_enqueries_found')}</p>
            <p className="mt-1 text-sm text-gray-500">{t('no_enqueries_description')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('company_name')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('full_name')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('phone')}</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {enqueries.map((enquery) => (
                  <tr key={enquery.id} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">{enquery.raison_social}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{enquery.full_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{enquery.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {enquery.seen_at ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCheck className="mr-1" size={12} />
                          {t('seen')}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleMarkAsSeen(enquery.id)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Eye className="mr-1" size={12} />
                          {t('mark_as_seen')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-6 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-700">
              {t('showing')} <span className="font-medium">{enqueries.length}</span> {t('of')}{' '}
              <span className="font-medium">{totalPages * 10}</span> {t('results')}
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('first')}</span>
                <ChevronsLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('previous')}</span>
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                {t('page')} {currentPage} {t('of')} {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('next')}</span>
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('last')}</span>
                <ChevronsRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}

